import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Mail, Lock, Send, ArrowLeft, CheckCircle } from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { authApi } from '@/services/api/auth';

const schema = z.object({
    email: z.string().min(1, 'El email es requerido').email('Ingresa un email válido'),
    password: z.string().min(8, 'La contraseña debe tener al menos 8 caracteres'),
    password_confirmation: z.string().min(8, 'Confirma tu contraseña'),
}).refine((data) => data.password === data.password_confirmation, {
    message: 'Las contraseñas no coinciden',
    path: ['password_confirmation'],
});

type FormData = z.infer<typeof schema>;

const ResetPasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get('token');

    const [serverMsg, setServerMsg] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const tokenMissing = useMemo(() => !token || token.trim().length === 0, [token]);

    const { register, handleSubmit, formState: { errors, isSubmitting }, setError, reset } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: { email: '', password: '', password_confirmation: '' },
    });

    const onSubmit = async (data: FormData) => {
        setServerMsg(null);
        if (tokenMissing) {
            setServerMsg('El enlace es inválido o ha expirado. Solicita uno nuevo.');
            return;
        }
        try {
            await (authApi as any).resetPassword({
                token: token as string,
                email: data.email,
                password: data.password,
                password_confirmation: data.password_confirmation,
            });
            setSuccess(true);
            setServerMsg('Tu contraseña fue actualizada correctamente. Ahora puedes iniciar sesión.');
            reset({ email: data.email, password: '', password_confirmation: '' });
        } catch (err: any) {
            const status = err?.response?.status;
            const payload = err?.response?.data;
            // 400 token inválido/expirado
            if (status === 400) {
                setServerMsg(payload?.message || payload?.error || 'El enlace es inválido o ha expirado.');
                return;
            }
            // 422 validaciones
            if (status === 422 && payload?.errors) {
                const fieldErrors = payload.errors as Record<string, string[]>;
                Object.entries(fieldErrors).forEach(([field, msgs]) => {
                    setError(field as keyof FormData, { message: msgs?.[0] || 'Campo inválido' });
                });
                setServerMsg(payload?.message || 'Corrige los errores e inténtalo otra vez.');
                return;
            }
            // 404 email no encontrado (si tu backend lo expone)
            if (status === 404) {
                setError('email', { message: 'No encontramos una cuenta con este correo.' });
                setServerMsg('Revisa tu email o crea una cuenta nueva.');
                return;
            }
            setServerMsg(payload?.message || payload?.error || err?.message || 'No se pudo restablecer la contraseña');
        }
    };

    if (tokenMissing) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="bg-white rounded-2xl shadow-xl p-8 text-center">
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">Enlace inválido</h2>
                        <p className="text-gray-600 mb-6">El token de recuperación no está presente o expiró. Solicita un nuevo correo de restablecimiento.</p>
                        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                            <Button onClick={() => navigate('/forgot-password')}>Solicitar nuevo enlace</Button>
                            <Button variant="secondary" onClick={() => navigate('/login')}>Volver al login</Button>
                        </div>
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="bg-white rounded-2xl shadow-xl p-8">
                    {/* Header (coherente con Login) */}
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-flex items-center space-x-2 mb-6">
                            <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center">
                                <span className="text-white font-bold text-xl">T</span>
                            </div>
                            <span className="text-2xl font-bold text-gray-900">Eisgemi</span>
                        </Link>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">Restablecer contraseña</h2>
                        <p className="text-gray-600">Ingresa tu correo y define una nueva contraseña.</p>
                    </div>

                    {!success ? (
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            <Input
                                {...register('email')}
                                type="email"
                                label="Email"
                                placeholder="tu@email.com"
                                leftIcon={<Mail className="w-5 h-5" />}
                                error={errors.email?.message}
                                fullWidth
                            />

                            <Input
                                {...register('password')}
                                type="password"
                                label="Nueva contraseña"
                                placeholder="••••••••"
                                leftIcon={<Lock className="w-5 h-5" />}
                                error={errors.password?.message}
                                fullWidth
                            />

                            <Input
                                {...register('password_confirmation')}
                                type="password"
                                label="Confirmar contraseña"
                                placeholder="••••••••"
                                leftIcon={<Lock className="w-5 h-5" />}
                                error={errors.password_confirmation?.message}
                                fullWidth
                            />

                            {serverMsg && (
                                <p className="text-sm text-red-600">{serverMsg}</p>
                            )}

                            <Button type="submit" loading={isSubmitting} fullWidth size="lg" rightIcon={<Send className="w-5 h-5" />}>
                                Guardar nueva contraseña
                            </Button>

                            <div className="flex items-center justify-between text-sm">
                                <Link to="/login" className="text-primary-600 hover:text-primary-500 inline-flex items-center gap-1">
                                    <ArrowLeft className="w-4 h-4" /> Volver a iniciar sesión
                                </Link>
                                <Link to="/register" className="text-primary-600 hover:text-primary-500">Crear cuenta</Link>
                            </div>
                        </form>
                    ) : (
                        <div className="space-y-6 text-center">
                            <div className="flex justify-center">
                                <CheckCircle className="w-12 h-12 text-green-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900">¡Contraseña actualizada!</h3>
                            <p className="text-gray-600">{serverMsg}</p>
                            <Button onClick={() => navigate('/login')} fullWidth size="lg">Ir a iniciar sesión</Button>
                        </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
